import {
  ErpOrderState,
  ErpOrderGroupSchema,
  getOrderPaymentOverview,
} from '../types/api/erp'
import { NumberUtils } from './NumberUtils'
import { GroupPayments } from './GroupPayments'
import { DEFAULT_DECIMALS } from '../currency'
import type { GroupPaymentMember, GroupPaymentOrder } from './GroupPayments'

export type HistoricalPaymentGroupMember = GroupPaymentMember & {
  customer_id: number
  group?: string | number | null
}

export type HistoricalPaymentGroupOrder = GroupPaymentOrder

export type HistoricalGroupLookup =
  | { status: 'failed' }
  | {
      status: 'ok'
      members: readonly HistoricalPaymentGroupMember[]
      hasMore: boolean
    }

export type HistoricalRetryPaymentResult =
  | {
      ok: true
      orders: HistoricalPaymentGroupOrder[]
      amount: number
    }
  | {
      ok: false
      reason:
        | 'lookup_failed'
        | 'inconsistent'
        | 'incomplete'
        | 'canceled'
        | 'unsupported_payment'
        | 'active_payment'
        | 'nothing_to_pay'
    }

export const getHistoricalPaymentGroupId = (order: {
  id: number
  group?: unknown
}) => ErpOrderGroupSchema.parse(order.group) ?? order.id

const getHistoricalOutstandingAmount = (
  member: HistoricalPaymentGroupMember,
  decimals: number
) => {
  const { total, paid } = getOrderPaymentOverview(member.items, member.payments)
  return Math.max(NumberUtils.round.decimal(total - paid, decimals), 0)
}

const getGroupOutstandingOrders = (
  members: readonly HistoricalPaymentGroupMember[],
  decimals: number
) => {
  const totals = GroupPayments.getTotals(members, decimals)
  if (!totals) {
    return null
  }
  return GroupPayments.allocateRemaining(
    totals.orders,
    totals.remaining,
    decimals
  )
}

const isMismatchedGroupMember = (
  member: HistoricalPaymentGroupMember,
  customerId: number,
  groupId: number
) => {
  if (member.customer_id !== customerId) {
    return true
  }

  const group = ErpOrderGroupSchema.parse(member.group)
  return group !== null && group !== groupId
}

export const canRetryHistoricalPayment = (
  member: HistoricalPaymentGroupMember,
  decimals = DEFAULT_DECIMALS
) => {
  if (
    member.status === ErpOrderState.cancel ||
    GroupPayments.hasActive(member.payments)
  ) {
    return false
  }
  if (getHistoricalOutstandingAmount(member, decimals) > 0) {
    return true
  }

  // Shared payments can make an individual order look paid. Check the group on tap.
  const hasGroup = ErpOrderGroupSchema.parse(member.group) !== null
  return hasGroup && GroupPayments.hasPosted(member.payments)
}

export const resolveHistoricalRetryPayment = ({
  selected,
  groupLookup,
  decimals = DEFAULT_DECIMALS,
}: {
  selected: HistoricalPaymentGroupMember
  groupLookup: HistoricalGroupLookup
  decimals?: number
}): HistoricalRetryPaymentResult => {
  if (groupLookup.status === 'failed') {
    return { ok: false, reason: 'lookup_failed' }
  }
  if (groupLookup.hasMore) {
    return { ok: false, reason: 'incomplete' }
  }

  const members = groupLookup.members
  const selectedMember = members.find((member) => member.id === selected.id)
  if (!selectedMember) {
    return { ok: false, reason: 'inconsistent' }
  }
  if (selectedMember.status === ErpOrderState.cancel) {
    return { ok: false, reason: 'canceled' }
  }

  const groupId = getHistoricalPaymentGroupId(selected)
  const hasMismatchedMembers = members.some((member) =>
    isMismatchedGroupMember(member, selected.customer_id, groupId)
  )
  if (hasMismatchedMembers) {
    return { ok: false, reason: 'inconsistent' }
  }

  const uniqueIds = new Set(members.map((member) => member.id))
  if (uniqueIds.size !== members.length) {
    return { ok: false, reason: 'inconsistent' }
  }

  if (members.some((member) => GroupPayments.hasActive(member.payments))) {
    return { ok: false, reason: 'active_payment' }
  }
  if (GroupPayments.hasPaidCanceledOrder(members)) {
    return { ok: false, reason: 'unsupported_payment' }
  }

  const orders =
    members.length > 1
      ? getGroupOutstandingOrders(members, decimals)
      : members
          .filter((member) => member.status !== ErpOrderState.cancel)
          .map((member) => ({
            id: member.id,
            ref: member.ref,
            amount: getHistoricalOutstandingAmount(member, decimals),
          }))
          .filter((order) => order.amount > 0)
          .sort((left, right) => left.id - right.id)

  if (orders === null) {
    return { ok: false, reason: 'inconsistent' }
  }

  if (orders.length === 0) {
    return { ok: false, reason: 'nothing_to_pay' }
  }

  const amount = NumberUtils.round.decimal(
    orders.reduce((acc, order) => acc + order.amount, 0),
    decimals
  )

  return { ok: true, orders, amount }
}
